import "./style.scss";
import Dropdown from "bootstrap/js/dist/dropdown";
import { compileConfig } from "./compile_form_config";
import { DiagnosticsPane } from "./diagnostics-ui";
import { EditMenu } from "./edit-menu";
import { EditTree } from "./edit-tree";
import { exportAsJsonSchema, exportAsXdf3 } from "./export";
import { SchemaValidator } from "./generator/schema";
import { SearchMenu } from "./search-menu";
import type { ProjectStore, SaveStatus } from "./store";

class NavLink {
  readonly root: HTMLElement;
  private link: HTMLAnchorElement;

  constructor(
    private onClick: () => void,
    icon: string,
    label: string,
  ) {
    this.root = document.createElement("li");
    this.root.classList.add("nav-item");

    const iconNode = document.createElement("i");
    iconNode.classList.add("bi", icon);

    this.link = document.createElement("a");
    this.link.classList.add(
      "nav-link",
      "px-3",
      "py-1",
      "text-black",
      "d-flex",
      "flex-row",
      "gap-2",
      "align-items-center",
    );
    this.link.href = "#";
    this.link.append(iconNode, label);

    this.root.appendChild(this.link);

    this.link.addEventListener("click", (event) => {
      event.preventDefault();
      this.onClick();
    });
  }

  public activate() {
    this.link.classList.add("bg-white");
  }

  public deactivate() {
    this.link.classList.remove("bg-white");
  }
}

class EditPage {
  readonly root: HTMLElement;

  readonly searchMenu: SearchMenu;
  readonly editMenu: EditMenu;
  readonly diagnostics: DiagnosticsPane;
  readonly editTree: EditTree;

  constructor(store: ProjectStore) {
    this.root = document.createElement("div");
    this.root.classList.add(
      "h-100",
      "d-flex",
      "gap-2",
      "pb-2",
      "flex-row",
      "align-items-stretch",
    );
    this.root.innerHTML = `
        <div id="left" class="d-flex flex-fill flex-column gap-1"></div>
        <div id="right" class="d-flex flex-column" style="width: 40em;"></div>
    `;

    const leftContainer = this.root.querySelector("#left")!;
    const rightContainer = this.root.querySelector("#right")!;

    this.searchMenu = new SearchMenu(store);
    this.editMenu = new EditMenu(store);
    this.diagnostics = new DiagnosticsPane(store);
    this.editTree = new EditTree(store, this.searchMenu, this.diagnostics);

    leftContainer.appendChild(this.editTree.root);
    leftContainer.appendChild(this.diagnostics.root);
    rightContainer.appendChild(this.editMenu.root);
    this.root.appendChild(this.searchMenu.root);
  }

  public detach() {
    this.editTree.detach();
    this.editMenu.detach();
    this.diagnostics.detach();
    // Nothing to do here
    // this.searchMenu.detach();
  }
}

class PreviewPage {
  public root: HTMLElement;

  private container: HTMLElement;

  constructor(private store: ProjectStore) {
    this.root = document.createElement("div");
    this.root.classList.add(
      "h-100",
      "d-flex",
      "flex-row",
      "align-items-stretch",
    );
    this.root.innerHTML = `
        <div class="container flex-grow-1">
        </div>
    `;

    this.container = this.root.querySelector(".container")!;

    this.store.addEventListener("project", this.update);
    this.update();
  }

  private update = () => {
    const editorDoc = this.store.toDocument();

    try {
      const config = compileConfig(editorDoc);
      const form = new SchemaValidator(config);
      this.container.replaceChildren(form.getRoot());
    } catch (error) {
      const errorContainer = document.createElement("div");
      errorContainer.classList.add("alert", "alert-danger", "mt-5");
      errorContainer.innerHTML = `
        <h4 class="alert-heading"></h4>
        <p><pre></pre></p>
      `;

      const title = errorContainer.querySelector("h4")!;
      title.textContent = "Fehler beim Generieren des Formulars";

      const message = errorContainer.querySelector("pre")!;
      message.textContent = `${error}`;

      this.container.replaceChildren(errorContainer);
    }
  };

  public detach() {
    this.store.removeEventListener("project", this.update);
  }
}

type Page = EditPage | PreviewPage;

export class Editor {
  public root: HTMLElement;

  private navBar: HTMLUListElement;
  private mainContent: HTMLDivElement;

  private activePageIndex: number;
  private activePage: Page | undefined = undefined;

  private navLinks: NavLink[];

  constructor(
    private store: ProjectStore,
    onClose: () => void,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add(
      "d-flex",
      "flex-column",
      "vh-100",
      "px-2",
      "bg-custom",
    );
    this.root.innerHTML = `
        <div class="d-flex flex-row py-3 align-items-center">
            <h1 class="fs-6 text-muted d-flex flex-row gap-2 align-items-center mb-0">
              <i class="bi bi-house-door"></i>
              <a href="#" id="home" class="link-underline link-underline-opacity-0">Start</a>
              <i class="bi bi-chevron-right"></i>
              <span class="text-body">${store.schema.fimId}</span>
            </h1>
                
            <div class="flex-fill d-flex flex-row justify-content-center">
                <ul id="main-links" class="nav nav-pills rounded bg-body-secondary border border-2 border-body-secondary">
                </ul>
            </div>

            <a href="mailto:info@x-gov.de" class="btn btn-outline-primary ms-auto me-2">
              Feedback <i class="bi bi-envelope-fill ms-2"></i>
            </a>
            <div class="dropdown">
              <button class="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown">
                <i class="bi bi-download me-1"></i> Herunterladen
              </button>

              <ul class="dropdown-menu">
                <li><a id="export-xdf3" class="dropdown-item d-flex flex-row align-items-center gap-2" href="#">
                  XDatenfelder 3.0.0
                  <span class="badge bg-warning text-warning-emphasis">
                    <i class="bi bi-exclamation-triangle-fill"></i> Beta
                  </span>
                </a></li>
                <li><a id="export-json-schema" class="dropdown-item" href="#">JSON Schema</a></li>
              </ul>
            </div>
        </div>

        <div class="flex-grow-1">
        </div>
    `;

    const homeLink: HTMLAnchorElement = this.root.querySelector("#home")!;
    homeLink.addEventListener("click", (event) => {
      event.preventDefault();
      onClose();
    });

    const dropdown: HTMLButtonElement =
      this.root.querySelector(".dropdown-toggle")!;
    new Dropdown(dropdown);

    const exportXdf3Link: HTMLAnchorElement =
      this.root.querySelector("#export-xdf3")!;
    exportXdf3Link.addEventListener("click", (event) => {
      event.preventDefault();
      const document = this.store.toDocument();
      exportAsXdf3(document);
    });

    const exportJsonSchemaLink: HTMLAnchorElement = this.root.querySelector(
      "#export-json-schema",
    )!;
    exportJsonSchemaLink.addEventListener("click", (event) => {
      event.preventDefault();
      const document = this.store.toDocument();
      exportAsJsonSchema(document);
    });

    this.navBar = this.root.querySelector("#main-links")!;
    this.mainContent = this.root.children[1] as HTMLDivElement;
    this.navLinks = [];

    this.addPage("bi-pencil", "Bearbeiten");
    this.addPage("bi-eye", "Vorschau");

    this.activePageIndex = 0;
    this.goToPage(0);
  }

  public getDocumentId(): string {
    return this.store.schema.fimId;
  }

  private addPage(icon: string, title: string) {
    const index = this.navLinks.length;

    const link = new NavLink(() => this.goToPage(index), icon, title);
    this.navLinks.push(link);
    this.navBar.append(link.root);
  }

  private goToPage(index: number) {
    this.navLinks[this.activePageIndex].deactivate();
    this.activePageIndex = index;
    this.navLinks[this.activePageIndex].activate();

    if (this.activePage !== undefined) {
      this.activePage.detach();
    }

    if (index === 0) {
      this.activePage = new EditPage(this.store);
    } else {
      this.activePage = new PreviewPage(this.store);
    }

    this.mainContent.replaceChildren(this.activePage.root);
  }

  public setSaveStatus(status: SaveStatus) {
    this.store.setSaveStatus(status);
  }
}
